import {
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TablePagination,
  Box,
  IconButton,
  useTheme,
  TableHead,
  TextField,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useState, MouseEvent, useRef } from "react";
import { KeyboardArrowRight, KeyboardArrowLeft } from "@mui/icons-material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import { useMutation, useQuery } from "react-query";
import { getBilledPatient, getBillPrint, getBillPrintV2 } from "../services";
import usePrint from "../../../hooks/usePrint";
import CustomTableBody from "../../../utilis/component/CustomTableBody";
import usePopup from "../../../hooks/usePopup";
import useAuthentication from "@hooks/useAuthentication";
import { useFetchPatientDetailsByEMR } from "../../../PatientCare/hookv2/apiHooks";

dayjs.extend(utc);
function TablePaginationActions(props: {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
}) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
    </Box>
  );
}

function BilledPatients() {
  const {user} = useAuthentication()
  const theme = useTheme();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const { printPdf, loading } = usePrint();
  const { showSnackbar } = usePopup();
  const [selected, setSelected] = useState<number>(-1);
   const [searchParams, setSearchParams] = useState<{
    filter: {
      search: string;
      searchDate: Dayjs;
    };
    pageSize: number;
    pageNumber: number;
  }>({
    filter: {
      search: "",
      searchDate: dayjs(),
    },
    pageSize: 25,
    pageNumber: 0,
  });
    const { data: billedData, isLoading: billedListLoading } = useQuery(
    ["getBilledPatient", searchParams],
    () => getBilledPatient(searchParams),
    {
      onError: (e: any) => {
        showSnackbar({
          message: e?.response?.data?.responseMessage || "Error !",
          open: true,
          variant: "error",
          duration: 4000,
        });
      },
      retry: 3,
    }
  );

const activeEncounterIdRef = useRef<number | null>(null);

  const { mutate: getPrint, isLoading: fileLoading } = useMutation(
    async ({ id, insName }: { id: number; insName: string }) => {
      console.log("user",user?.codetype)
       return user?.codetype === "CPT" 
         ?(await getBillPrint(id)) : (await getBillPrintV2({
          insname:insName,
          clinicId:Number(user?.clinicId),
          encounterId:id,
         }));
     }, 
    {
      onSuccess: (result) => {
        console.clear()
        if (result.responseIndicator=="success") {

          printPdf(result?.response?.invoice as string);
        }
      },
      onError: (error: any) => {
        console.log("error", error);
      },
    }
  );
  const { mutate: getPatientByEmr, isLoading: patientDataLoading } = useFetchPatientDetailsByEMR({
  onSuccess: (res: any) => {
    const currentId = activeEncounterIdRef.current;
    if (res.responseIndicator === "success" && currentId) {
      const insName = res.response?.insurancePatient?.insurance_name || "Cash";
      getPrint({ id: currentId as number, insName: insName });
    }
  }
});

  const handleChangePage = (
    _: MouseEvent<HTMLButtonElement> | null,
    pageNumber: number
  ) => {
    setSearchParams((prev) => ({
      ...prev,
      pageNumber,
    }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchParams((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      pageNumber: 0,
    }));
  };

  const handlePrint = (e: MouseEvent<HTMLButtonElement>) => {
    const id = e.currentTarget.getAttribute("data-patientid") as string;
    const index = e.currentTarget.getAttribute("data-index") as string;
     const emrNo = e.currentTarget.getAttribute("data-emrno") as string;
      // const insName = e.currentTarget.getAttribute("data-insname") as string;

      const numericId = parseInt(id);
      activeEncounterIdRef.current = numericId;
        getPatientByEmr({
      emrNo:emrNo
    });
    setSelected(parseInt(index));
  };

//  const regionValue= user?.region;
//    const idHeading =
//   regionValue === "QATAR_REGION"
//     ? "Qatar ID"
//     : "Emirates Id";
const idHeading = "Civil ID"
  const heading = [
  "Sl no",
  "Emr No/Reg No",
  "Name",
  idHeading,
  "Registration Date",
  "Action",
];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="flex gap-3 flex-wrap p-2 items-end justify-start">
        <div className="">
          <TextField
            label="Search"
            onChange={(e) => {
              setSearchParams((prev) => ({
                ...prev,
                filter: {
                  ...prev.filter,
                  search: e.target.value,
                },
              }));
            }}
          />
        </div>
        <div className="">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DateTimePicker"]}>
              <DatePicker
                // disabled={
                //   timeSlot.slotType != "dayGridMonth" &&
                //   timeSlot.slotType != "multiMonthYear"
                // }
                label={
                  <span>
                    Date
                    <span style={{ color: theme.palette.error.main }}>*</span>
                  </span>
                }
                format="DD-MM-YYYY"
                timezone="UTC"
                value={dayjs(searchParams.filter.searchDate)}
                onChange={(date) => {
                  setSearchParams((prev) => ({
                    ...prev,
                    filter: {
                      ...prev.filter,
                      searchDate: date || dayjs(),
                    },
                  }));
                }}
                slotProps={{
                  textField: {
                    //   error: !!error,
                    //   helperText: error?.message,
                    //   InputProps: { readOnly: true },
                  },
                }}
              />
            </DemoContainer>
          </LocalizationProvider>
        </div>
      </div>
      <TableContainer sx={{ flexGrow: 1, overflow: "auto", px: 1 }}>
        <div className="h-[calc(100vh-270px)]">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {heading.map((item, index) => (
                  <TableCell
                    key={index}
                    sx={(theme) => ({
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.background.paper,
                    })}
                  >
                    {item}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <CustomTableBody
              alert={"No records"}
              dataLoading={billedListLoading}
              isData={Boolean(billedData?.response?.items?.length)}
              totalSpan={{ col: heading.length, row: 5 }}
            >
              {billedData?.response?.items.map((row, i) => (
                <TableRow key={i + 1}>
                  <TableCell>
                    {(searchParams.pageNumber as number) *
                      (searchParams.pageSize as number) +
                      (i + 1)}
                  </TableCell>
                  <TableCell>{row.emrNo}</TableCell>
                  <TableCell>{row.patientName}</TableCell>
                  <TableCell>{row.emiratesId}</TableCell>
                  <TableCell>
                    {dayjs(row.registrationDate).format("DD-MM-YYYY hh:mm A")}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Print" placement="left">
                      {(fileLoading || loading ||patientDataLoading) && selected == i ? (
                        <CircularProgress size={18} />
                      ) : (
                        <IconButton
                          disabled={fileLoading || loading ||patientDataLoading}
                          color="primary"
                          data-patientid={row.encounterId}
                          data-index={i}
                          data-emrno={row.emrNo}
                          data-insname={row.emrNo}
                          onClick={handlePrint}
                        >
                          <LocalPrintshopIcon />
                        </IconButton>
                      )}
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {/* {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )} */}
            </CustomTableBody>
          </Table>
        </div>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={billedData?.response?.totalCount || 0}
        rowsPerPage={searchParams.pageSize || 0}
        page={searchParams.pageNumber || 0}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
      />
      <iframe
        ref={iframeRef}
        className="hidden"
        width="100%"
        height="600px"
      ></iframe>
    </Box>
  );
}

export default BilledPatients;

